import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ExploreSearchParams } from "@/routes/explore/index";

import { CloseIcon, RadioIcon } from "./icons";

interface OptionProps {
  isSelected: boolean;
  label: string;
  onClick(): void;
}

function Option({ label, isSelected, onClick }: OptionProps) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: dis good
    // biome-ignore lint/a11y/noNoninteractiveElementInteractions: legacy
    <li
      className="relative flex cursor-pointer items-center justify-between text-white"
      onClick={onClick}
    >
      <p className="text-14">{label}</p>
      <div className="size-5 rounded-full border border-white/20" />
      <RadioIcon
        className={cn(
          "absolute top-0 right-0 transition-opacity",
          !isSelected && "opacity-0"
        )}
      />
    </li>
  );
}

interface OptionsModalProps extends React.ComponentProps<typeof Dialog> {
  initialState: boolean | null;
}

export default function OptionsModal({
  initialState,
  ...props
}: OptionsModalProps) {
  const [isScary, setIsScary] = useState<boolean | null>(initialState);

  const search = useSearch({ strict: false }) as ExploreSearchParams;
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const handleApply = () => {
    navigate({
      search: {
        ...search,
        is_scary: isScary ? "Yes" : "No",
      },
      to: pathname,
    });
    props.onOpenChange?.(false);
  };

  const handleClear = () => {
    const { is_scary: _, ...newSearch } = search;

    navigate({
      search: newSearch,
      to: pathname,
    });
    props.onOpenChange?.(false);
  };

  const handleOption = (value: boolean) => () => setIsScary(value);

  return (
    <Dialog {...props}>
      <DialogContent
        className="flex size-full flex-col gap-0 bg-[url(/images/body.png)] bg-midnight-express bg-size-[300%] bg-top md:mt-39.5 md:ml-67 md:h-auto md:max-w-sm md:rounded-[15px] md:bg-twilight-indigo"
        overlayClassName="md:justify-start md:items-start"
      >
        <DialogHeader className="relative border-white/10 border-b">
          <Button
            aria-label="Close"
            className="absolute top-1/2 left-6 size-[25px] -translate-y-1/2"
            onClick={() => props.onOpenChange?.(false)}
            type="button"
          >
            <CloseIcon />
          </Button>
          <DialogTitle className="text-center font-semibold text-16/[65px] text-white">
            How scary?
          </DialogTitle>
        </DialogHeader>
        <div className="px-6 py-5">
          <ul className="space-y-3.5">
            <Option
              isSelected={isScary === false}
              label="Not scary 🐣"
              onClick={handleOption(false)}
            />
            <Option
              isSelected={isScary === true}
              label="Scary 🔪"
              onClick={handleOption(true)}
            />
          </ul>
        </div>
        <DialogFooter className="mt-auto flex flex-row items-center justify-between border-white/10 border-t bg-space-gray-dark px-6 py-5 sm:justify-between md:bg-transparent">
          <Button
            className="font-medium text-14 text-white underline"
            onClick={handleClear}
            type="button"
          >
            Clear all
          </Button>
          <Button
            className="h-11 rounded-[22px] px-12.5"
            disabled={isScary === null}
            onClick={handleApply}
            type="submit"
            variant="cta"
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
